// Release-cut confirm modal. Opens → fetches the pre-cut preview (per-repo
// labeled-PR counts) so the engineer sees exactly what will be folded before
// firing. Confirm → POST /release-cuts (202) → navigate to the REL-<date>
// ticket page where the per-repo lanes render.

import { useCallback, useEffect, useState } from "react";
import api from "../../api";
import Icon from "./icons";
import { Modal, apiErrorMessage } from "./shared";

export default function ReleaseCutModal({ open, onClose, onCut }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setErr(null);
      return;
    }
    let alive = true;
    setLoading(true);
    api
      .bssReleaseCutPreview()
      .then((res) => {
        if (alive) setPreview(res.data);
      })
      .catch((e) => {
        if (alive)
          setErr(apiErrorMessage(e, "Could not load the release preview."));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open]);

  const repos = (preview && preview.repos) || [];
  const withPrs = repos.filter((r) => r.labeledCount > 0);
  const totalPrs = withPrs.reduce((a, r) => a + r.labeledCount, 0);
  // The configured label (RELEASE_LABEL) — "release" in prod, "test-release"
  // during rollout. Shown so the modal never lies about what it matches.
  const label = (preview && preview.label) || "release";

  const confirm = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.bssReleaseCutTrigger();
      onCut && onCut(res.data);
      onClose && onClose();
    } catch (e) {
      const data = e?.response?.data;
      if (data?.error === "cut_in_progress") {
        setErr("A release cut is already running for today.");
      } else if (data?.error === "disabled") {
        setErr("Release cuts are disabled (RELEASE_CUT_ENABLED=false).");
      } else {
        setErr(apiErrorMessage(e, "Could not start the release cut."));
      }
    } finally {
      setBusy(false);
    }
  }, [onClose, onCut]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cut release"
      width={520}
      footer={
        <>
          <button
            type="button"
            className="bss-btn bss-btn--ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bss-btn bss-btn--primary"
            onClick={confirm}
            disabled={busy || loading || withPrs.length === 0}
            title={
              withPrs.length === 0
                ? "No open PRs carry the release label"
                : `Cut ${preview?.branch || "the release branch"}`
            }
          >
            <Icon.GitMerge size={14} />{" "}
            {busy ? "Cutting…" : `Cut ${preview?.branch || "release"}`}
          </button>
        </>
      }
    >
      {loading && (
        <div style={{ color: "var(--bss-fg-muted)" }}>Loading preview…</div>
      )}
      {err && (
        <div style={{ color: "var(--bss-danger)", marginBottom: 10 }}>
          {err}
        </div>
      )}
      {preview && !loading && (
        <>
          <div style={{ fontSize: 14, marginBottom: 12 }}>
            Builds <span className="bss-code-inline">{preview.branch}</span> in
            each repo and merges every open PR labeled{" "}
            <span className="bss-code-inline">{label}</span> into it. Opens a
            draft PR → base; <b>nothing merges to master</b>.
          </div>
          {withPrs.length === 0 ? (
            <div style={{ color: "var(--bss-fg-muted)" }}>
              No open PRs carry the{" "}
              <span className="bss-code-inline">{label}</span> label right now —
              nothing to cut.
            </div>
          ) : (
            <table className="bss-table">
              <thead>
                <tr>
                  <th>Repo</th>
                  <th style={{ textAlign: "right", width: 110 }}>
                    Labeled PRs
                  </th>
                </tr>
              </thead>
              <tbody>
                {withPrs.map((r) => (
                  <tr key={r.repoId}>
                    <td>{r.repoId}</td>
                    <td style={{ textAlign: "right" }}>{r.labeledCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {withPrs.length > 0 && (
            <div
              style={{
                color: "var(--bss-fg-subtle)",
                fontSize: 12,
                marginTop: 8
              }}
            >
              {totalPrs} PR{totalPrs === 1 ? "" : "s"} across {withPrs.length}{" "}
              repo
              {withPrs.length === 1 ? "" : "s"}. Conflicts are flagged, not
              force-merged.
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
